import React, { useEffect, useRef, useState } from 'react';
import { config } from './config';
import { CLOUD_PROVIDER_SERVICES } from '../../utils/constants';
import { useFetchData } from '../../hooks/useFetchData';
import { useDispatch, useSelector } from 'react-redux';
import { accountState, AppDispatch, RootState } from '../../store';
import { ALL_VALUE } from '../Invoice/config';
import { format } from 'date-fns';
import './billingConsole.css';
import TitleHeader from '../../components/MasterComponent/TitleHeader/TitleHeader';
import CollapsibleTable from '../../components/MasterComponent/CollapsibleTable/CollapsibleTable';
import MonthlyBillingView from './MonthlyBillingView';
import BillingHeader from '../../components/BillingConsole/BillingHeader';
import { setSelectedMonth } from '../../store/monthSlice';

const BillingConsole: React.FC = () => {
  const selectedAccountInfo = useSelector(accountState);
  type ViewMode = 'monthview' | 'previousview' | 'dailyview';

  type ServiceItem = {
    name: string;
    rate: string;
    usage: string;
    amount: string;
  };

  type Region = {
    regionName: string;
    items: ServiceItem[];
  };

  type ServiceData = {
    serviceName: string;
    total: string;
    regions: Region[];
  };

  // Define view modes configuration for BillingConsole
  const viewModes = [
    {
      key: 'monthview',
      label: 'Monthly View',
      buttonText: 'Monthly View',
    },
    {
      key: 'previousview',
      label: 'Previous Months',
      buttonText: 'Previous Months',
    },
    {
      key: 'dailyview',
      label: 'Daily Breakup',
      buttonText: 'Daily Breakup',
    },
  ];

  const [viewMode, setViewMode] = useState<ViewMode>('monthview');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const selectedMonth = useSelector((state: RootState) => state.month.selectedMonth);
  const monthOptions = useSelector((state: RootState) => state.month.monthOptions);
  const monthValue = selectedMonth || (monthOptions[0] ?? '');
  const [selectedAccountId, setSelectedAccountId] = React.useState<any>(
    selectedAccountInfo.selectedAccountId
  );

  const [servicesData, setServicesData] = useState<ServiceData[]>([]);
  const [openServices, setOpenServices] = useState<Record<string, boolean>>({});
  const [openRegions, setOpenRegions] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const reqBodyRef = useRef({
    view: viewMode,
    month: format(new Date(selectedMonth), 'yyyy-MM-01'),
    formate: 'detailed',
    ...(selectedAccountId !== ALL_VALUE
      ? { accountId: selectedAccountId }
      : {}),
  });

  const { fetch, data, loader: isSubmitting } = useFetchData({
    fetchData: () => config.getBillingConsoleReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  const toggleService = (serviceName: string) => {
    setOpenServices((prev) => ({
      ...prev,
      [serviceName]: !prev[serviceName],
    }));
  };

  const handleToggleAll = () => {
    const expand = !isExpanded;
    setIsExpanded(expand);

    // Expand/collapse all services
    const allServices = servicesData.reduce((acc, item) => {
      acc[item.serviceName] = expand;
      return acc;
    }, {} as Record<string, boolean>);
    setOpenServices(allServices);

    // Expand/collapse all regions inside each service
    const allRegions = servicesData.reduce((regionAcc, service) => {
      regionAcc[service.serviceName] = service.regions.reduce(
        (innerAcc, region) => {
          innerAcc[region.regionName] = expand;
          return innerAcc;
        },
        {} as Record<string, boolean>
      );
      return regionAcc;
    }, {} as Record<string, Record<string, boolean>>);
    setOpenRegions(allRegions);
  };

  const handleViewModeChange = (newViewMode: string) => {
    setViewMode(newViewMode as ViewMode);
  };

  // account selection section
  const onAccountChange = (data: any) => {
    if (data.cloudType === CLOUD_PROVIDER_SERVICES.AWS)
      setSelectedAccountId(data.accountId);
  };

  const getCurrentViewLabel = () => {
    const currentView = viewModes.find((vm) => vm.key === viewMode);
    return currentView ? currentView.label : 'Monthly View';
  };

  useEffect(() => {
    reqBodyRef.current = {
      view: viewMode,
      month: format(new Date(selectedMonth), 'yyyy-MM-01'),
      formate: 'detailed',
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };

    // Clear expanded states
    setOpenServices({});
    setOpenRegions({});
    setIsExpanded(false);

    fetch();
  }, [viewMode, selectedAccountId, selectedMonth]);

  let isCanceled = false;

  useEffect(() => {
    if (data && !isSubmitting && !isCanceled) {
      setServicesData(data);
    }

    return () => {
      isCanceled = true;
    };
  }, [data, isSubmitting]);

  return (
    <div className="app-container">
      <TitleHeader
        extraContent={
          <BillingHeader
            viewModes={viewModes}
            currentViewMode={viewMode}
            selectedMonth={monthValue}
            onViewModeChange={handleViewModeChange}
            onMonthChange={(e) => dispatch(setSelectedMonth(e.target.value))}
            onAccountChange={onAccountChange}
            isSubmitting={isSubmitting}
          />
        }
      >
        {getCurrentViewLabel()}
      </TitleHeader>

      {viewMode === 'monthview' && (
        <MonthlyBillingView
          servicesData={servicesData ?? []}
          isLoading={isSubmitting}
          isExpanded={isExpanded}
          openServices={openServices}
          openRegions={openRegions}
          onToggleAll={handleToggleAll}
          onToggleService={toggleService}
          setOpenRegions={setOpenRegions}
        />
      )}

      {viewMode === 'previousview' && (
        <CollapsibleTable
          data={servicesData}
          isLoading={isSubmitting}
          className="customTableStyle"
        />
      )}

      {viewMode === 'dailyview' && (
        <CollapsibleTable
          data={servicesData}
          isLoading={isSubmitting}
          className="customTableStyle"
        />
      )}
    </div>
  );
};

export default BillingConsole;
